import { useMemo } from "react";
import { Divider, Stack, Typography } from "@mui/material";
import DataCell from "@/components/DataCell";
import { format } from "date-fns";
import Price from "@/components/Price";
import { useAppSelector } from "@/hooks";
import { getOrderDetails, getOrderItemPrice, getOrderSubtotal, getOrderTaxes, getOrderTotal } from "@/utils/order";
import { Cash, Flow, Order, OrderItem } from "@/types";
import { getFirstAndLastName } from "@/utils/names";
import { formatPhone } from "@/utils/phones";
import { es } from "date-fns/locale";
import { MENU_FLOW_LABELS } from "@/constants/global";
import useNeighborhood from "@/hooks/useNeighborhood";
import useServiceFee from "@/hooks/useServiceFee";

interface Props {
  order: Order;
}

const ReceiptCustomerTemplate = ({ order }: Props) => {
  const { getNeighborhoodName } = useNeighborhood();
  const organizationId = useAppSelector((store) => store.ui.activeOrganization);
  const currentOrganization = useAppSelector((store) => store.organizations[organizationId] ?? null);
  const service = useServiceFee();

  const tips = useMemo(() => {
    if (order?.areTipsIncluded && order?.tipPercentage) {
      return order.areTipsIncluded ? getOrderSubtotal(order.items as OrderItem[]) * (order.tipPercentage / 100) : 0;
    }
    return order?.areTipsIncluded ? 0 : 0;
  }, [order?.areTipsIncluded, order?.items, order?.tipPercentage]);

  return (
    <Stack className="receipt-template" spacing={2} padding={3} bgcolor="white" width="100%">
      <Stack alignItems="center">
        <Typography variant="h2" fontWeight={700} fontSize={"16px"}>
          {currentOrganization?.title}
        </Typography>
        <Typography variant="h3" fontSize={"14px"}>Cliente [ {MENU_FLOW_LABELS[(order?.flow ?? "") as unknown as keyof typeof MENU_FLOW_LABELS] ?? String(order?.flow ?? "N/A")} ]</Typography>
      </Stack>
      <Stack>
        {order?.branch ? <DataCell label="Sucursal" value={order?.branch?.title as unknown as string} fontSize={'14px'} /> : null}
        <DataCell label="Orden" value={order?.number as unknown as string} fontSize={'14px'} />
        <DataCell label="Hora" value={order?.archivedAt ? format(order.archivedAt, "p") : order?.lastUpdatedAt ? format(order.lastUpdatedAt, "p") : "N/A"} fontSize={'14px'} />
        <DataCell label="Fecha" value={order?.archivedAt ? format(order.archivedAt, "dd/MM/yyyy") : order?.lastUpdatedAt ? format(order.lastUpdatedAt, "dd/MM/yyyy") : "N/A"} fontSize={'14px'} />
        {(order?.flow as string) === Flow.PICKUP ? (
          <DataCell label="Hora de recogida" value={order?.scheduleTime ? format(order.scheduleTime.toDate(), " hh:mm a", { locale: es }) : "N/A"} fontSize={'14px'} />
        ) : null}
      </Stack>
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" borderTop={2} borderBottom={2}>
          <Typography variant="caption" fontWeight="bold" fontSize={'14px'}>
            ITEMS
          </Typography>
          <Typography variant="caption" fontWeight="bold" fontSize={'14px'}>
            MONTOS
          </Typography>
        </Stack>
        {order?.items?.map((item: OrderItem) => (
          <Stack key={item.id} direction="row" spacing={2} justifyContent="space-between">
            <Stack spacing={1} direction="row">
              <Typography flex={1} fontSize={'14px'}>{item.quantity}</Typography>
              <div>
                <Typography fontWeight="bold" flex={1} fontSize={'14px'}>
                  {item.title}
                </Typography>
                <Typography fontSize={'14px'}>{getOrderDetails(item)}</Typography>
              </div>
            </Stack>
            <Typography variant="body2" fontSize={"14px"}>
              <Price currency="NIO">{getOrderItemPrice(item)}</Price>
            </Typography>
          </Stack>
        ))}
        {currentOrganization?.settings?.includeTax ? (
          <>
            <DataCell label="Subtotal"
              value={<Price currency="NIO">{getOrderSubtotal(order?.items)}</Price>}
              fontSize={'14px'} />
            <DataCell label="Impuestos (IVA)"
              value={<Price currency="NIO">{getOrderTaxes(order?.items)}</Price>}
              fontSize={'14px'} />
          </>
        ) : null}
        {order.flow === "delivery" && service.type !== "fixed" ? (
          order?.deliveryFee ? (
            <DataCell label="Envío"
              value={<Price currency="NIO">{order?.deliveryFee}</Price>}
              fontSize={"14px"} />
          ) : (
            <Stack border={2} paddingX={1} paddingY={1}>
              <DataCell label="Envío" value="" fontSize={"14px"} />
            </Stack>
          )
        ) : null}
        <DataCell
          label="Total"
          value={<Price currency="NIO">{order?.deliveryFee ? getOrderTotal(order.items as OrderItem[], currentOrganization?.settings?.includeTax, Number(order?.deliveryFee)) : getOrderTotal(order.items as OrderItem[], currentOrganization?.settings?.includeTax, Number(service.fee))}</Price>}
          fontSize={'14px'}
        />
        {(order?.paymentDetails?.value as Cash)?.amount ? <DataCell label="Paga con" value={<Price currency="NIO">{(order?.paymentDetails?.value as Cash)?.amount}</Price>} fontSize={'14px'} /> : null}
        <Divider sx={{ borderColor: "black" }} />
        <DataCell label="Cliente:" value={getFirstAndLastName(order?.client?.name + " " + order?.client?.lastname)} fontSize={'14px'} />
        <DataCell label="Teléfono:" value={formatPhone(order?.client?.phone, 505)} fontSize={'14px'} />
        {order?.flow !== Flow.PICKUP && order?.flow !== Flow.DINE_IN ? (
          <>
            {order?.deliveryLocation?.house ? <DataCell label="Casa:" value={order?.deliveryLocation?.house} fontSize={'14px'} /> : null}
            {order?.deliveryLocation?.neighborhood ? <DataCell label="Barrio:" value={getNeighborhoodName(order?.deliveryLocation?.neighborhood)} fontSize={'14px'} /> : null}
            {order?.deliveryLocation?.address ? <DataCell label="Dirección:" value={order?.deliveryLocation?.address} fontSize={'14px'} /> : null}
          </>
        ) : null}
      </Stack>

      <Divider>
        <Typography variant="body2" fontWeight="bold" fontSize={"14px"}>¡Gracias por tu preferencia!</Typography>
      </Divider>
    </Stack>
  );
};

export default ReceiptCustomerTemplate;
